import { inject } from '@angular/core';
import { ActivityEntityName, DomainType, EntityViewDisplay, FeatureFlags, JobSecurityRight, QuotePro, QuoteSecurityRight, SecurityModuleTag, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import type { EntityManifestRoutes } from '@workbuddy/services/entities/entity.manifest.relations';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { entityRecordUrl } from '@workbuddy/ui/v2/blocks/entity.record';
import { quoteStatusTag } from './presentation/quote.status.colour';

// Registered when this manifest module is loaded at bootstrap. Consumers resolve manifests via
// getManifest/EntityManifestService only.
//
// There is ONE quote product with TWO identifiers, so this file registers it twice. The EF manifest
// registers as QuotePro, but QuotePro's own live subsystems — activities, merge templates, document
// themes, custom fields (see module.jms.quotes.pro quote.convert / quote.dashboard) — still stamp
// and look the record up under ActivityEntityName.Quote. Both names must therefore resolve to the
// same card, or a reference written under one identifier renders blank when read under the other.
// One shared `card` const guarantees they cannot drift.
const card: EntityManifestCard<QuotePro> = {
  component: () => import('./presentation/card.quote').then(m => m.CardQuoteComponent),
  resolution: 'snapshot',
  summarize: quote => ({ title: quote.quoteNumber ?? 'Not set' }),
};

function canAccessQuotes(rights: RightsService): boolean {

  return rights.hasRights(QuoteSecurityRight.CanAccess) || rights.hasRights(JobSecurityRight.AccessQuote);
}

function canManageQuotes(rights: RightsService): boolean {

  return rights.hasRights(QuoteSecurityRight.Manage) || rights.hasRights(JobSecurityRight.ManageQuote);
}

// Shared for the same reason as `card`: a link stamped under either identifier opens the same page.
const routes: EntityManifestRoutes = {
  root: '/quotes/quotes-new',
  record: id => entityRecordUrl('quotes', 'preview', id),
};

export const quoteManifest = registerManifest<QuotePro>({
  entity: ActivityEntityName.QuotePro,
  slug: 'quotes',
  featureFlag: FeatureFlags.FinanceDocsQuoteV2,
  menu: {
    group: 'quotes',
    label: 'Quotes',
    icon: 'fa-calculator',
    order: 10,
    badge: SecurityModuleTag.Beta,
    authorize: () => inject(RightsService).hasRights(QuoteSecurityRight.CanAccess),
  },
  routes,

  // tags feeds the BOARD CARD's status badge; the surface header reads the same colours through the
  // same quoteStatusTag function, so card and header cannot drift apart.
  presentation: {
    card,
    tags: { status: quoteStatusTag },
  },

  // A domain replaces the tenant entry wholesale, so each portal restates card + tags.
  domains: {
    [DomainType.ClientPortal]: {
      presentation: {
        card,
        tags: { status: quoteStatusTag },
      },
    },
    [DomainType.ContractorPortal]: {
      presentation: {
        card,
        tags: { status: quoteStatusTag },
      },
    },
  },

  board: {
    // Kanban is not meaningful for financial documents; locked manage preview is a per-view toggle.
    supportedDisplays: [EntityViewDisplay.Card, EntityViewDisplay.Table],
    // Document-shaped: the selection shows in the wide ~50/50 aside, not a narrow quickview.
    panel: { kind: 'preview' },
    defaults: { header: { title: 'Quotes' } },
  },

  surfaces: {
    [SurfaceType.FinancialDocument]: () => import('./surface/manage/quote.manage.surface').then(m => m.quoteSurface),
  },

  // Job-linked quotes are granted through the Job module's quote rights, so either module's right opens one.
  operations: {
    create: { surface: SurfaceType.FinancialDocument, authorize: ({ injector }) => canManageQuotes(injector.get(RightsService)) },
    // Read access mounts the document; write rights and lifecycle belong to the surface.
    edit: { surface: SurfaceType.FinancialDocument, authorize: ({ injector }) => canAccessQuotes(injector.get(RightsService)) },
    preview: { surface: SurfaceType.FinancialDocument, authorize: ({ injector }) => canAccessQuotes(injector.get(RightsService)) },
  },

  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/quote.activities.relation').then(m => m.quoteActivitiesBoardBuilder),
  },

  // Surface declares Flag/ContactLink sections; this is host context only (not enablement).
  sectionRelations: {
    [ActivityEntityName.ContactLink]: {
      allowExisting: true,
      allowAdhoc: true,
      relationshipTags: true,
      parentScope: quote => [quote.account?._id, quote.site?._id].filter((id): id is string => !!id),
      globalSearch: false,
      // Customer Details owns this contact; related-link actions must not replace or remove it.
      primary: { read: quote => quote.contact, canSet: false },
    },
  },
});

// The same product under its other identifier. QuotePro above owns the `quote` slug, surfaces and
// operations; this alias uses a unique slug solely to avoid colliding with that canonical route
// identity. It declares no operations, so nothing mounts through it; its links open the quote's page.
export const quoteEntityNameManifest = registerManifest<QuotePro>({
  entity: ActivityEntityName.Quote,
  slug: 'legacy-quote',
  routes,

  presentation: { card },

  // A domain replaces the tenant entry wholesale, so each portal restates the card.
  domains: {
    [DomainType.ClientPortal]: { presentation: { card } },
    [DomainType.ContractorPortal]: { presentation: { card } },
  },
});
